// Right-side composer icon cluster: Attach · code block · Sticker · Emoji.
//
// All icon buttons are 7×7 with the same hover treatment so they
// read as one cluster, mirroring the screenshot.

import {
  Code2,
  Paperclip,
  Smile,
  Sticker,
} from "lucide-react";
import { useRef, type ReactNode } from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@link/ui";
import { EmojiPicker } from "./EmojiPicker";
import { GifButton } from "./GifPicker";

interface Props {
  /** Inserts text into the active composer (e.g. on emoji pick). */
  onInsert?: (text: string) => void;
  /** Fires when the user attaches a file from the disk picker. */
  onAttach?: (files: File[]) => void;
}

export function ComposerActions({ onInsert, onAttach }: Props) {
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <AttachButton onAttach={onAttach} />
      <GifButton onInsert={onInsert} />
      <div className="hidden sm:block"><CodeButton onInsert={onInsert} /></div>
      <div className="hidden sm:block"><StickerButton onInsert={onInsert} /></div>
      <EmojiButton onInsert={onInsert} />
    </div>
  );
}
function CodeButton({ onInsert }: { onInsert?: (text: string) => void }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => onInsert?.("```yaml\nmessage: write here\n```")}
          className="flex h-7 w-7 items-center justify-center rounded-md text-cloud-500 hover:bg-strawberry-50 hover:text-strawberry-600"
          aria-label="Insert code block"
        >
          <Code2 className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent>Insert a fenced code block</TooltipContent>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Attach
// ---------------------------------------------------------------------------

function AttachButton({ onAttach }: { onAttach?: (files: File[]) => void }) {
  const input = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        className="sr-only"
        tabIndex={-1}
        onChange={(event) => {
          const files = Array.from(event.currentTarget.files ?? []);
          if (files.length > 0) onAttach?.(files);
          event.currentTarget.value = "";
        }}
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => input.current?.click()}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-cloud-500 hover:bg-strawberry-50 hover:text-strawberry-600 sm:h-8 sm:w-8"
            aria-label="Attach files"
          >
            <Paperclip className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Attach a file</TooltipContent>
      </Tooltip>
    </>
  );
}

// ---------------------------------------------------------------------------
// Sticker
// ---------------------------------------------------------------------------

const STICKERS: ReadonlyArray<{ id: string; emoji: string; label: string }> = [
  { id: "s1", emoji: "🍓", label: "strawberry" },
  { id: "s2", emoji: "☁️", label: "cloud" },
  { id: "s3", emoji: "✨", label: "sparkle" },
  { id: "s4", emoji: "🦴", label: "bone" },
  { id: "s5", emoji: "🌷", label: "tulip" },
  { id: "s6", emoji: "🍰", label: "cake" },
  { id: "s7", emoji: "🐾", label: "paws" },
  { id: "s8", emoji: "🎀", label: "bow" },
  { id: "s9", emoji: "🌙", label: "moon" },
  { id: "s10", emoji: "🍡", label: "dango" },
  { id: "s11", emoji: "🪼", label: "jelly" },
  { id: "s12", emoji: "🧁", label: "cupcake" },
];

function StickerButton({ onInsert }: { onInsert?: (text: string) => void }) {
  return (
    <Popover>
      <Tooltip>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="flex h-7 w-7 items-center justify-center rounded-md text-cloud-500 hover:bg-strawberry-50 hover:text-strawberry-600"
              aria-label="Pick a sticker"
            >
              <Sticker className="h-4 w-4" />
            </button>
          </TooltipTrigger>
        </PopoverTrigger>
        <TooltipContent>Send a sticker</TooltipContent>
      </Tooltip>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={10}
        className="w-[min(320px,calc(100vw-1.5rem))] overflow-hidden border-cloud-200 bg-white p-0"
      >
        <PickerHeading>Strawberry Pack · Default</PickerHeading>
        <div className="grid grid-cols-4 gap-2 px-3 pb-3">
          {STICKERS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onInsert?.(s.emoji)}
              title={s.label}
              className="flex h-16 items-center justify-center rounded-lg border border-cloud-200 bg-cloud-50 text-3xl transition-colors hover:border-strawberry-300 hover:bg-strawberry-50"
            >
              {s.emoji}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Emoji
// ---------------------------------------------------------------------------

function EmojiButton({ onInsert }: { onInsert?: (text: string) => void }) {
  return <EmojiPicker onSelect={(emoji) => onInsert?.(emoji)}><button type="button" className="flex h-10 w-10 items-center justify-center rounded-lg text-cloud-500 hover:bg-strawberry-50 hover:text-strawberry-600 sm:h-8 sm:w-8" aria-label="Pick an emoji"><Smile className="h-4 w-4" /></button></EmojiPicker>;
}

// ---------------------------------------------------------------------------
// Tiny composables shared by every picker.
// ---------------------------------------------------------------------------

function PickerHeading({ children }: { children: ReactNode }) {
  return (
    <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-cloud-500">
      {children}
    </p>
  );
}
